import { SlidersHorizontal, X } from 'lucide-react'
import React, { useState } from 'react'

import { Sheet, SheetContent } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

import { Button } from '../../primitives/Button'

interface AdvancedFiltersDrawerProps {
  /**
   * Callback when filters are applied
   * Return false to prevent closing the drawer (e.g., validation failed)
   */
  onApply: () => boolean | undefined
  /**
   * Callback when filters are cleared
   */
  onClear: () => void
  /**
   * Number of active filters to display in badge
   */
  activeFiltersCount?: number
  /**
   * Trigger button text
   */
  triggerLabel?: string
  /**
   * Drawer title
   */
  title?: string
  /**
   * Apply button text
   */
  applyLabel?: string
  /**
   * Clear button text
   */
  clearLabel?: string
  /**
   * Filter content (children)
   */
  children: React.ReactNode
  /**
   * Custom className for the trigger button
   */
  triggerClassName?: string
  /**
   * Custom width for the drawer (default: 400px)
   */
  drawerWidth?: string
}

/**
 * AdvancedFiltersDrawer - Reusable sidebar drawer for advanced filters
 *
 * Based on Figma design with:
 * - Sidebar from the right
 * - Fixed header with title and close button
 * - Scrollable content area
 * - Fixed footer with action buttons
 * - Dividers between filter sections
 *
 * Usage:
 * ```tsx
 * <AdvancedFiltersDrawer
 *   onApply={handleApplyFilters}
 *   onClear={handleClearFilters}
 *   activeFiltersCount={5}
 * >
 *   <FilterItem icon={<Building2 />} title="Compañía" label="Seleccionar">
 *     <SearchableSelectExpanded ... />
 *   </FilterItem>
 *   <div className="h-px w-full bg-neutral-300" />
 *   <FilterItem icon={<Tag />} title="Categoría" label="Seleccionar">
 *     <SearchableSelectExpanded ... />
 *   </FilterItem>
 * </AdvancedFiltersDrawer>
 * ```
 */
export function AdvancedFiltersDrawer({
  onApply,
  onClear,
  activeFiltersCount = 0,
  triggerLabel = 'Filtros avanzados',
  title = 'Filtros avanzados',
  applyLabel = 'Aplicar',
  clearLabel = 'Limpiar',
  children,
  triggerClassName,
  drawerWidth = '400px',
}: AdvancedFiltersDrawerProps) {
  const [open, setOpen] = useState(false)

  const handleApply = () => {
    const result = onApply()
    // Only close if onApply returns true or undefined (default behavior)
    // Return false to keep drawer open (e.g., validation failed)
    if (result !== false) {
      setOpen(false)
    }
  }

  const handleClear = () => {
    onClear()
    setOpen(false)
  }

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      {/* Trigger Button */}
      <Button
        aria-label={triggerLabel}
        className={cn(
          'gap-2 border-[#D4D4D4] bg-[#FAFAFA] shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
          'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]',
          triggerClassName
        )}
        onClick={() => setOpen(true)}
        size="md"
        variant="outline"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <span>
          {triggerLabel}
          {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
        </span>
      </Button>

      {/* Drawer Content */}
      <SheetContent
        className="flex flex-col gap-0 p-0 [&>button]:hidden"
        side="right"
        style={{ width: drawerWidth, maxWidth: '100vw' }}
      >
        {/* Header - Fixed */}
        <div
          className={cn(
            'flex h-[52px] w-full flex-col items-start justify-between gap-2',
            'border-b border-[#D4D4D4] bg-[#FAFAFA] p-4'
          )}
        >
          {/* AL Container */}
          <div className="flex h-6 w-full flex-row items-center justify-between gap-2">
            {/* Title */}
            <h2
              className="text-[20px] font-semibold leading-[120%] tracking-[-0.02em] text-[#262626]"
              style={{ fontFamily: 'Geist' }}
            >
              {title}
            </h2>

            {/* Close Button */}
            <button
              aria-label="Cerrar"
              className="flex h-4 w-4 flex-shrink-0 items-center justify-center p-0 hover:opacity-70"
              onClick={() => setOpen(false)}
              type="button"
            >
              <X className="h-4 w-4 text-[#737373]" strokeWidth={1.33} />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div
          className={cn(
            'scrollbar-thumb-rounded-full flex-1 overflow-y-auto',
            'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-400',
            'bg-[#FAFAFA] px-6',
            'touch-pan-y'
          )}
          onTouchMove={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {children}
        </div>

        {/* Footer - Fixed */}
        <div
          className={cn(
            'flex items-center justify-between gap-3 border-t',
            'border-neutral-300 bg-white px-6 py-4'
          )}
        >
          <Button
            className="flex-1"
            onClick={handleClear}
            size="md"
            variant="outline"
          >
            {clearLabel}
          </Button>
          <Button className="flex-1" onClick={handleApply} size="md">
            {applyLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
